import {
    ColDef,
    ExcelExportParams,
    FirstDataRenderedEvent,
    ICellRendererParams,
    ITooltipParams,
    SideBarDef,
    ValueGetterParams,
} from 'ag-grid-community';
import { AgGridReactProps } from 'ag-grid-react';
import { UNMAPPED } from 'business/constants';
import _ from 'lodash';
import BottomPinnedRowRenderer from 'routes/HumanResourcesModule/components/HrDataGrid/BottomPinnedRowRenderer';
import {
    INDICIAIRE,
    MANTY_STATUS,
    ORG_CHART_HR_MAPPING,
    ORG_CHART_HR_NAME,
    RECURRENCE,
    RETIREMENT_SYSTEM,
} from 'routes/HumanResourcesModule/business/definitions';
import NumberFloatingFilter from 'ui/AgGridReact/NumberFloatingFilter';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import { TEXT_FILTER } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/defaultColDefs';
import { DateString } from 'routes/HumanResourcesModule/business/types';
import LastYearComparatorNumberCellRenderer from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/components/cellRenderers/LastYearComparatorCellRenderer/LastYearComparatorNumberCellRenderer';
import ChipCellRenderer from 'ui/AgGridReact/CellRenderers/ChipCellRenderer';
import {
    getPositionValueFromField,
    HR_POSITION_MAPPING_VALUE_GETTER_FIELD,
    recurrentFormatter,
} from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/gridHelpers';
import EnvelopeAgentCellRenderer from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/cellRenderers/EnvelopeAgentCellRenderer';
import { t } from 'i18next';
import { ENVELOPE_PAY_COMPONENT_HYPOTHESIS } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import {
    agentFilterValueGetter,
    agentValueFormatter,
    AGENT_FIELD,
} from './agentColumnHelpers';
import {
    orgChartHrFormatter,
    orgChartHrValueFormatter,
} from './orgChartHrColumnHelpers';
import { isFooterRow } from 'routes/HumanResourcesModule/Simulation/helpers/gridHelpers';
import AgentNameWithTooltipCellRenderer from '../../AgentNameWithTooltipCellRenderer';

export const formatTitleToExport = (title: string) => {
    return title.replace('.', '_');
};
export type MonthlyColDefGetterArguments = {
    dateColumns: DateString[];
    isMonthly: boolean;
    exercice: number;
    isForPermanentAgent: boolean;
    showLastYearColumns: boolean;
    shouldDisplayPermanent?: boolean;
    t: TranslationT;
};

const DEFAULT_COL_DEF = {
    filter: false,
    sortable: true,
    resizable: true,
};

export const LAST_YEAR_COLUMN_STYLE = { color: 'grey' };

export const GREY_HEADER_CLASS = 'grey-header';
export const LIGHTGRAY_HEADER_CLASS = 'lightgray-header';

const sideBar = {
    toolPanels: [
        {
            id: 'columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
                suppressRowGroups: true,
                suppressValues: true,
                suppressPivots: true,
                suppressPivotMode: true,
                suppressColumnFilter: true,
                suppressColumnSelectAll: true,
                suppressColumnExpandAll: true,
            },
            width: 300,
        },
    ],
} as SideBarDef;

const onFirstDataRendered = (params: FirstDataRenderedEvent) => {
    const allColGroup = params.api.getAllDisplayedColumnGroups();
    const allCols = params.api.getAllDisplayedColumns();
    if ((allColGroup && allColGroup.length < 6) || allCols.length < 8) {
        params.api.sizeColumnsToFit();
    }
};

export const DEFAULT_GRID_PROPS: AgGridReactProps = {
    className: 'ag-theme-material',
    domLayout: 'autoHeight',
    onFirstDataRendered,
    suppressDragLeaveHidesColumns: true,
    components: {
        bottomPinnedRowRenderer: BottomPinnedRowRenderer,
        lastYearComparatorNumberCellRenderer:
            LastYearComparatorNumberCellRenderer,
        textFloatingFilter: TextFloatingFilter,
        numberFloatingFilter: NumberFloatingFilter,
        chipCellRenderer: ChipCellRenderer,
        envelopeAgentCellRenderer: EnvelopeAgentCellRenderer,
        agentNameWithTooltipCellRenderer: AgentNameWithTooltipCellRenderer,
    },
    pagination: true,
    paginationPageSize: 10,
    sideBar,
    defaultColDef: {
        ...DEFAULT_COL_DEF,
    },
    rowHeight: 32,
    headerHeight: 36,
    tooltipShowDelay: 750,
    groupDisplayType: 'multipleColumns',
    rowGroupPanelShow: 'always',
};
const isGroupedColumn = (params: ICellRendererParams) =>
    params.colDef?.rowGroup;

const isFirstColumn = (params: ValueGetterParams | ICellRendererParams) =>
    params?.column
        ? params.api.getDisplayedColBefore(params.column) === null
        : false;

/**
 * Gets the value to be displayed in non-grouped cells of a grouped column.
 * For the 'orgChartHrMapping' column, it displays the 'orgChartHrName' if an 'orgChartHrMapping' exists.
 * This means the mapping is displayed in the column, not the name, but only in the case of services.
 * @param params - Parameters providing context about the cell, including data and column information.
 * @returns The value to be displayed in the cell or undefined if no specific value is determined.
 */
const autoGroupValueGetter = (params: ValueGetterParams) => {
    const rowField = params.colDef?.showRowGroup;
    const colId = params.column.getColId();

    // Check if the column is 'orgChartHrMapping' and if the row is not part of a grouped field
    if (colId.includes(ORG_CHART_HR_MAPPING) && typeof rowField === 'string') {
        // Display 'orgChartHrName' if 'orgChartHrMapping' exists
        if (params.data?.orgChartHrMapping) {
            return params.data?.orgChartHrName;
        }
        return undefined;
    }
    return undefined;
};

export const getHrAutoGroupColDef = (): ColDef => ({
    ...TEXT_FILTER,
    cellRendererParams: {
        suppressCount: true,
    },
    width: 250,
    cellRendererSelector: (params) =>
        params.node?.rowPinned === 'bottom' &&
        !(isGroupedColumn(params) && !isFirstColumn(params))
            ? { component: 'bottomPinnedRowRenderer' }
            : { component: 'agGroupCellRenderer' },
    rowGroup: true,
    pinned: 'left',
    lockPosition: 'left',
    valueGetter: autoGroupValueGetter,
    tooltipValueGetter: (params: ITooltipParams) => {
        if (
            params?.value &&
            params?.value?.orgChartHrName?.includes('..unmapped')
        )
            return t('humanResources.obsoleteService');

        return '';
    },
    filterValueGetter: (params: ValueGetterParams) => {
        const rowField = params.colDef?.showRowGroup;
        const colId = params.column.getColId();
        const matchedPositionMapping = [
            INDICIAIRE,
            RETIREMENT_SYSTEM,
            MANTY_STATUS,
        ].find((positionMapping) => colId.includes(positionMapping)) as
            | HR_POSITION_MAPPING_VALUE_GETTER_FIELD
            | undefined;

        if (matchedPositionMapping) {
            return getPositionValueFromField(matchedPositionMapping, params);
        }
        if (colId.includes(ORG_CHART_HR_MAPPING)) {
            return orgChartHrFormatter({
                orgChartHrMapping: params?.data?.[ORG_CHART_HR_MAPPING] || '',
                orgChartHrName: params?.data?.[ORG_CHART_HR_NAME] || '',
            });
        }
        if (colId.includes(AGENT_FIELD)) {
            return agentFilterValueGetter(params);
        }
        return typeof rowField === 'string' ? params.data?.[rowField] : '';
    },
});

/**
 * Gets the default export parameters for Excel.
 *
 * This is necessary because ag-Grid does not use value formatters
 * during the Excel export. Therefore, we must manually process each cell
 * to ensure that the values are formatted as they appear in the grid.
 *
 * @returns {ExcelExportParams} The default parameters for Excel export.
 */
const getDefaultExportParams = (): ExcelExportParams => ({
    processCellCallback: (params) => {
        const { field } = params.column.getColDef();
        if (field === AGENT_FIELD) return agentValueFormatter(params);
        if (field === ORG_CHART_HR_MAPPING) {
            return orgChartHrValueFormatter(params);
        }
        if (field === RECURRENCE) return recurrentFormatter(params);
        if (field === ENVELOPE_PAY_COMPONENT_HYPOTHESIS) {
            if (!params.value) return '';
            return t('humanResources.simulation.editHypothesis');
        }
        return params.value;
    },
});

export const getDefaultGridProps = (): AgGridReactProps => ({
    ...DEFAULT_GRID_PROPS,
    autoGroupColumnDef: getHrAutoGroupColDef(),
    defaultExcelExportParams: getDefaultExportParams(),
});

const extractRawDateYear = (dateStr: DateString): number =>
    parseInt(dateStr.substring(0, 4), 10);

const extractRawDateMonth = (dateStr: DateString): number =>
    parseInt(dateStr.substring(4, 6), 10);

// cell renderer selector for the tables having a date ventilation toggle (monthly or yearly)
export const dateVentilatedTablesCellRendererSelector =
    (isMonthly: boolean, exercice: number, date: DateString) =>
    (params: ICellRendererParams) => {
        if (isFooterRow(params)) return { component: undefined };
        if (params.node.rowPinned)
            return { component: 'bottomPinnedRowRenderer' };
        if (!isMonthly || extractRawDateYear(date) !== exercice)
            return { component: undefined };
        return { component: 'lastYearComparatorNumberCellRenderer' };
    };

const sortDatesByDecreasingOrder = (dates: DateString[]): DateString[] =>
    dates.sort((a, b) => parseInt(b, 10) - parseInt(a, 10));

// sort date strings  by increasing month and  increasing year for columns of the same month
// the result will be month n-1, month n, month + 1 n -1, month + 1 n
const sortByIncreasingMonthAndYear = (dates: DateString[]): DateString[] =>
    dates.sort((a: DateString, b: DateString): number => {
        const monthA = extractRawDateMonth(a);
        const monthB = extractRawDateMonth(b);
        if (monthA === monthB) {
            return extractRawDateYear(a) - extractRawDateYear(b);
        }
        return monthA - monthB;
    });

export const getDateColumnsForVentilation = (
    dateColumns: DateString[],
    isMonthly: boolean,
) =>
    isMonthly
        ? sortByIncreasingMonthAndYear(dateColumns)
        : sortDatesByDecreasingOrder(dateColumns);

/**
 * When we display the columns by month, for each column of exercice n
 * we want to able to show the column associated to the same month at exercice n-1
 * but we don't want to have n-1 columns not associated to a n equivalent
 * (we don't want december n-1 without december n for example). So we filter those columns.
 */
const filterLastYearDatesForUpcomingMonths = (
    dates: DateString[],
    exercice: number,
): DateString[] => {
    const currentYearMonths = new Set(
        dates
            .filter((date) => extractRawDateYear(date) === exercice)
            .map((date) => extractRawDateMonth(date)),
    );
    return dates.filter(
        (date) =>
            extractRawDateYear(date) === exercice ||
            currentYearMonths.has(extractRawDateMonth(date)),
    );
};

export function extractDates<DateT extends string>(
    data: Record<PivotedField<string, DateT>, unknown>[],
): DateT[] {
    if (data.length === 0) return [];
    const dateKeys = Object.keys(data[0]).filter((key) => key.includes('..'));
    return _(dateKeys.map((key) => key.split('..')[1]))
        .sort((a, b) => {
            const dateA = new Date(a);
            const dateB = new Date(b);
            return dateA.getTime() - dateB.getTime();
        })
        .uniq()
        .value() as DateT[];
}

export function filterAndExtractDates(
    data: Record<string, unknown>[],
    isMonthly: boolean,
    exercice: number,
): DateString[] {
    const dates = extractDates<DateString>(data);

    return isMonthly
        ? filterLastYearDatesForUpcomingMonths(dates, exercice)
        : dates;
}

// returns true if column is issued from the 'show last year' toggle (available only in monthly ventilation)
export const isColumnFromLastYearToggle = (
    date: DateString,
    isMonthly: boolean,
    exercice: number,
): boolean => isMonthly && extractRawDateYear(date) !== exercice;

export const shouldHideColumnByDefault = (
    showLastYearColumns: boolean,
    isLastYearColumn: boolean,
): boolean => !showLastYearColumns && isLastYearColumn;

export const permanentJobFormatter = ({ value }: { value: string }) =>
    value === UNMAPPED ? '-' : value;

export const DEFAULT_ROW_GROUP_COLUMN: ColDef = {
    ...TEXT_FILTER,
    width: 125,
    pinned: 'left',
};
